import { ColumnDef } from '@tanstack/react-table'
import { Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ProviderUser } from '@magnetar/admin-api-interfaces'
import { providerUsersService } from '@/services/provider_users_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { useState } from 'react'
import { ProviderUserPermissionDialog } from './ProviderUserPermissionDialog'
import { EditProviderUserDialog } from './EditProviderUserDialog'
import { DeleteAlert } from '@/components/DeleteAlert'

type ProviderUserTableMeta = {
  reloadData?: () => void
}

export const columns: ColumnDef<ProviderUser>[] = [
  {
    accessorKey: 'provider_user_id',
    header: 'ID',
  },
  {
    accessorKey: 'user.account',
    header: '帳號',
  },
  {
    accessorKey: 'user.user_name',
    header: '名稱',
  },
  {
    accessorKey: 'user.email',
    header: 'Email',
    cell: ({ row }) => {
      const email = row.original.user?.email
      return email || '-'
    },
  },
  {
    accessorKey: 'provider.provider_name',
    header: 'Provider 名稱',
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const providerUser = row.original
      const { token } = useAuth()
      const [showEditDialog, setShowEditDialog] = useState(false)
      const [showPermissionDialog, setShowPermissionDialog] = useState(false)
      const [showDeleteAlert, setShowDeleteAlert] = useState(false)

      const handleDelete = async () => {
        if (!token) return

        const result = await providerUsersService.deleteProviderUser(
          providerUser.provider_id,
          providerUser.provider_user_id,
          token,
        )

        if (result.success) {
          toast.success('刪除 Provider 使用者成功')
          ;(table.options.meta as ProviderUserTableMeta)?.reloadData?.()
        } else {
          toast.error(result.error?.message || '刪除 Provider 使用者失敗')
        }
      }

      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>操作</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                編輯
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowPermissionDialog(true)}>
                權限設定
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowDeleteAlert(true)} className="text-destructive">
                刪除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* 編輯對話框 */}
          <EditProviderUserDialog
            providerUser={providerUser}
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            onSuccess={() => (table.options.meta as ProviderUserTableMeta)?.reloadData?.()}
          />

          {/* 權限設定對話框 */}
          <ProviderUserPermissionDialog
            providerUser={providerUser}
            open={showPermissionDialog}
            onOpenChange={setShowPermissionDialog}
            onSuccess={() => (table.options.meta as ProviderUserTableMeta)?.reloadData?.()}
          />

          {/* 刪除確認對話框 */}
          <DeleteAlert
            open={showDeleteAlert}
            onOpenChange={setShowDeleteAlert}
            onConfirm={handleDelete}
            title="確定要刪除此 Provider 使用者嗎？"
            description="此操作無法復原。這將永久刪除此使用者的設定和所有相關資料。"
          />
        </>
      )
    },
  },
]
